import { useMemo } from 'react'

import RenderHTML from '../../components/ui-components/RenderHTML'
import {
  investmentExistsButtons as staticInvestmentExistsButtons,
  investmentDoesNotExistButtons as staticInvestmentDoesNotExistButtons,
  InvestmentExistsButtonConfig,
  InvestmentDoesNotExistButtonConfig
} from '../../utils/mf-execution/buttonsConfig'
import {
  MFExecutionData,
  ActionButton,
  PaymentPageType,
  SubActionDisabledReason,
  OnboardingStatus,
  RegularPlanSheetVariant
} from '../../types/mf-execution.types'
import { useMetaDataContext } from '../../state-management/contexts/MetaDataContext'
import { PowerPerformanceForms } from '../../types/mutual-fund-detail.types'
import { postCTA, trackEvent } from '../../utils/appBridge'
import { EventNames } from '../../types/misc.types'

export const useActionButtons = (
  openActionsSheet: (action: 'invest' | 'withdraw' | 'more-options') => void,
  openNotRecommendedSheet: (
    type: 'OFF_TRACK' | 'OUT_OF_FORM',
    actionType?: PaymentPageType.BUY | PaymentPageType.SIP
  ) => void,
  openWithdrawalLearnMoreSheet: () => void,
  handleActionButtonClick: (action: 'invest' | 'withdraw' | 'more-options') => void,
  handlePaymentPageOpen: (paymentType: PaymentPageType) => void,
  openRegularSheet: (variant: RegularPlanSheetVariant) => void,
  proceedToInvestActions: () => void,
  currentAction: 'invest' | 'withdraw' | 'more-options' | null,
  data: MFExecutionData
) => {
  const { powerForm, isUserElite, isin, data: metadata } = useMetaDataContext()
  const fundIsin = isin ?? ''
  const fundName = metadata?.fundDetails?.fundName ?? ''
  const isPortfolioInsightsLocked = metadata?.portfolioInsightsLockedInfo?.status === 'LOCKED'
  // Off-track / out-of-form warnings are Elite performance insights. Skip on the locked page
  // (non-elite or lapsed Elite) so execution goes straight to invest / payment.
  const shouldShowNotRecommendedSheet = isUserElite && !isPortfolioInsightsLocked

  // Memoized investment button builders with proper dependenciesm
  const investmentExistsButtons = useMemo((): ActionButton[] => {
    return staticInvestmentExistsButtons.map((button: InvestmentExistsButtonConfig): ActionButton => {
      if (button.type === 'more-options') {
        return {
          testLocator: button.testLocator,
          icon: <RenderHTML innerHTMLText={button.icon ?? ''} />,
          onClick: (e?: React.MouseEvent) => {
            e?.stopPropagation()
            trackEvent({
              eventName: EventNames.ButtonTap,
              payload: {
                ScreenName: 'MF_L2',
                eventId: button.testLocator,
                buttonLabel: button.text ?? 'More options',
                fundIsin,
                fundName
              }
            })
            if (data?.executionOnboardingStatus === OnboardingStatus.NOT_ONBOARDED) {
              postCTA(JSON.stringify(data?.cta))
              return
            }
            handleActionButtonClick('more-options')
            openActionsSheet('more-options')
          },
          isPrimary: button.isPrimary || false,
          isDisabled: data?.buttonDisabledStates?.moreOptionsButton?.isDisabled ?? false,
          isActive: currentAction === 'more-options',
          width: button.width
        }
      } else if (button.type === 'withdraw') {
        return {
          testLocator: button.testLocator,
          text: button.text,
          onClick: (e?: React.MouseEvent) => {
            e?.stopPropagation()
            trackEvent({
              eventName: EventNames.ButtonTap,
              payload: {
                ScreenName: 'MF_L2',
                eventId: button.testLocator,
                buttonLabel: button.text ?? 'Withdraw',
                fundIsin,
                fundName
              }
            })
            if (data?.executionOnboardingStatus === OnboardingStatus.NOT_ONBOARDED) {
              postCTA(JSON.stringify(data?.cta))
              return
            }
            // if (
            //   !data?.buttonDisabledStates?.withdrawButton?.isDisabled &&
            //   data?.buttonDisabledStates?.withdrawButton?.disabledReason
            // ) {
            //   openWithdrawalLearnMoreSheet()
            //   return
            // }
            if (data?.buttonDisabledStates?.sell?.disabledReason === SubActionDisabledReason.REGULAR) {
              openRegularSheet(RegularPlanSheetVariant.WITHDRAW)
              return
            }
            handlePaymentPageOpen(PaymentPageType.WITHDRAW)
          },
          isDisabled: data?.buttonDisabledStates?.sell?.isDisabled || data?.sell?.folioDetails?.length === 0,
          // onClick: (e?: React.MouseEvent) => {
          //   e?.stopPropagation()
          //   if (data?.executionOnboardingStatus === OnboardingStatus.NOT_ONBOARDED) {
          //     postCTA(JSON.stringify(data?.cta))
          //     return
          //   }
          //   if (
          //     !data?.buttonDisabledStates?.withdrawButton?.isDisabled &&
          //     data?.buttonDisabledStates?.withdrawButton?.disabledReason
          //   ) {
          //     openWithdrawalLearnMoreSheet()
          //     return
          //   }
          //   handleActionButtonClick('withdraw')
          //   openActionsSheet('withdraw')
          // },
          // isDisabled: data?.buttonDisabledStates?.withdrawButton?.isDisabled,
          // isActive: currentAction === 'withdraw'
          isPrimary: button.isPrimary || false,
        }
      } else if (button.type === 'invest') {
        return {
          testLocator: button.testLocator,
          text: button.text,
          onClick: (e?: React.MouseEvent) => {
            e?.stopPropagation()
            trackEvent({
              eventName: EventNames.ButtonTap,
              payload: {
                ScreenName: 'MF_L2',
                eventId: button.testLocator,
                buttonLabel: button.text ?? 'Invest',
                fundIsin,
                fundName
              }
            })
            if (data?.executionOnboardingStatus === OnboardingStatus.NOT_ONBOARDED) {
              postCTA(JSON.stringify(data?.cta))
              return
            }
            if (data?.buttonDisabledStates?.investButton?.disabledReason === SubActionDisabledReason.REGULAR) {
              openRegularSheet(RegularPlanSheetVariant.INVEST)
              return
            } else if (shouldShowNotRecommendedSheet && powerForm === PowerPerformanceForms.OFF_TRACK) {
              openNotRecommendedSheet('OFF_TRACK')
            } else if (shouldShowNotRecommendedSheet && powerForm === PowerPerformanceForms.OUT_OF_FORM) {
              openNotRecommendedSheet('OUT_OF_FORM')
            } else {
              proceedToInvestActions()
            }
          },
          isPrimary: button.isPrimary || false,
          isDisabled: data?.buttonDisabledStates?.investButton?.isDisabled,
          isActive: currentAction === 'invest'
        }
      }

      return {
        testLocator: button.testLocator,
        text: button.text,
        onClick: () => { },
        isPrimary: button.isPrimary || false,
        isDisabled: false,
        isActive: false
      }
    })
  }, [
    openActionsSheet,
    powerForm,
    isUserElite,
    shouldShowNotRecommendedSheet,
    data?.buttonDisabledStates,
    openRegularSheet,
    fundIsin,
    fundName,
  ])

  const investmentDoesNotExistButtons = useMemo((): ActionButton[] => {
    return staticInvestmentDoesNotExistButtons.map((button: InvestmentDoesNotExistButtonConfig): ActionButton => {
      if (button.type === 'one-time') {
        return {
          testLocator: button.testLocator,
          text: button.text,
          onClick: (e?: React.MouseEvent) => {
            e?.stopPropagation()
            trackEvent({
              eventName: EventNames.ButtonTap,
              payload: {
                ScreenName: 'MF_L2',
                eventId: button.testLocator,
                buttonLabel: button.text ?? 'One-time',
                fundIsin,
                fundName
              }
            })
            if (data?.executionOnboardingStatus === OnboardingStatus.NOT_ONBOARDED) {
              postCTA(JSON.stringify(data?.cta))
              return
            }
            if (data?.buttonDisabledStates?.buy?.disabledReason === SubActionDisabledReason.REGULAR) {
              openRegularSheet(RegularPlanSheetVariant.INVEST)
              return
            }
            if (shouldShowNotRecommendedSheet && powerForm === PowerPerformanceForms.OFF_TRACK) {
              openNotRecommendedSheet('OFF_TRACK', PaymentPageType.BUY)
            } else if (shouldShowNotRecommendedSheet && powerForm === PowerPerformanceForms.OUT_OF_FORM) {
              openNotRecommendedSheet('OUT_OF_FORM', PaymentPageType.BUY)
            } else {
              handlePaymentPageOpen(PaymentPageType.BUY)
            }
          },
          isPrimary: button.isPrimary || false,
          isDisabled: data?.buttonDisabledStates?.buy?.isDisabled,
          isActive: false
        }
      } else if (button.type === 'start-sip') {
        return {
          testLocator: button.testLocator,
          text: button.text,
          onClick: (e?: React.MouseEvent) => {
            e?.stopPropagation()
            trackEvent({
              eventName: EventNames.ButtonTap,
              payload: {
                ScreenName: 'MF_L2',
                eventId: button.testLocator,
                buttonLabel: button.text ?? 'Start SIP',
                fundIsin,
                fundName
              }
            })
            if (data?.executionOnboardingStatus === OnboardingStatus.NOT_ONBOARDED) {
              postCTA(JSON.stringify(data?.cta))
              return
            }
            if (data?.buttonDisabledStates?.buy?.disabledReason === SubActionDisabledReason.REGULAR) {
              openRegularSheet(RegularPlanSheetVariant.INVEST)
              return
            }
            if (shouldShowNotRecommendedSheet && powerForm === PowerPerformanceForms.OFF_TRACK) {
              openNotRecommendedSheet('OFF_TRACK', PaymentPageType.SIP)
            } else if (shouldShowNotRecommendedSheet && powerForm === PowerPerformanceForms.OUT_OF_FORM) {
              openNotRecommendedSheet('OUT_OF_FORM', PaymentPageType.SIP)
            } else {
              handlePaymentPageOpen(PaymentPageType.SIP)
            }
          },
          isPrimary: button.isPrimary || false,
          isDisabled: data?.buttonDisabledStates?.sip?.isDisabled,
          isActive: false
        }
      }

      return {
        testLocator: button.testLocator,
        text: button.text,
        onClick: () => { },
        isPrimary: button.isPrimary || false,
        isDisabled: false,
        isActive: false
      }
    })
  }, [
    data?.buttonDisabledStates?.buy?.isDisabled,
    data?.buttonDisabledStates?.sip?.isDisabled,
    data?.buttonDisabledStates?.buy?.disabledReason,
    data?.buttonDisabledStates?.sip?.disabledReason,
    powerForm,
    shouldShowNotRecommendedSheet,
    openRegularSheet,
    fundIsin,
    fundName
  ])

  return {
    investmentExistsButtons,
    investmentDoesNotExistButtons
  }
}
